import { useState } from 'react'

import { PrimaryButton, UpsellCTA } from '@swaggerhub-portal/component-library'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useRouteLoaderData } from 'react-router'

import DarkModeToggle from '../DarkModeToggle'
import CreateProductModal from '~/components/ProviderCreateProductModal'
import { useApiHubPlan } from '~/hooks/useApiHubPlan'
import { usePageLayout } from '~/hooks/usePageLayout'
import { useShubOrgInfo } from '~/hooks/useShubOrgInfo'
import { PortalLandingPageLoader } from '~/routes/_docs+/loader'
import { PreviewMode } from '~/types/preview'
import { isTemplatePreviewMode } from '~/utils/shared/preview'

export const PreviewTopBar = ({ mode }: { mode: PreviewMode }) => {
  const { previewTopBar } = usePageLayout()
  const { products = [] } = useRouteLoaderData<PortalLandingPageLoader>('routes/_docs+/_index') || {}

  const { productname } = useParams()

  const { organizationRole } = useShubOrgInfo()

  const {
    templatesEnabled,
    getUpgradeUrl,
    getUpgradeLabel,
  } = useApiHubPlan()

  const [newProductModalOpen, setNewProductModalOpen] = useState(false)

  const isTemplatePreview = isTemplatePreviewMode(mode)

  const { t } = useTranslation()

  return (
    <>
      <div 
        aria-label={t('consumer.preview.topBar.ariaLabel')}
        className={clsx(
          'flex items-center justify-between gap-4 px-4 font-semibold bg-neutral-95 dark:bg-neutral-50 text-neutral-10 dark:text-neutral-98',
          isTemplatePreview ? 'py-3.5' : 'py-4',
        )}
      >
        <div className="text-h4">
          {isTemplatePreview ? t('consumer.preview.topBar.template.header') : t('consumer.preview.topBar.header')}
        </div>
        <div className="flex items-center gap-4">
          {!previewTopBar?.productContent?.disabled && (
            <div className="ml-auto text-body2">
              {t('consumer.preview.topBar.productContent.description')}
              <Link to={products.length > 0 ? `/sp-admin/products/${products[0].slug}/edit/` : '/sp-admin/home'} className="pl-2 underline" reloadDocument>
                {t('consumer.preview.topBar.productContent.button')}
              </Link>
            </div>
          )}
          <DarkModeToggle />
          {isTemplatePreview && (
            <div className="flex items-center gap-2">
              <PrimaryButton
                size="s"
                onClick={() => setNewProductModalOpen(true)}
                disabled={!templatesEnabled}
                disabledClassName="pointer-events-none cursor-not-allowed bg-neutral-20 dark:bg-neutral-20 text-neutral-50 dark:text-neutral-50"
              >
                {t('consumer.preview.topBar.template.useTemplate.label')}
              </PrimaryButton>
              {!templatesEnabled && (
                <UpsellCTA
                  tooltip={t('entitlements.gates.default.tooltip')}
                  linkHref={getUpgradeUrl(organizationRole)}
                  linkText={getUpgradeLabel(organizationRole)}
                  className={clsx('text-accent-40 dark:text-neutral-00')}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {isTemplatePreview && (
        <CreateProductModal
          isOpen={newProductModalOpen}
          onClose={() => setNewProductModalOpen(false)}
          initialTemplateId={productname}
        />
      )}
    </>
  )
}